/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  ClipboardList, Plus, Search, Calendar, Users, BarChart3,
  Clock, CheckCircle2, FileText, Eye, ChevronRight, Loader2,
  TrendingUp, ListChecks, Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  taslak: { label: 'Taslak', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
  aktif: { label: 'Aktif', color: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  pasif: { label: 'Pasif', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  tamamlandi: { label: 'Tamamlandi', color: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
};

const SurveyCard = ({ survey, showStatus = true, canViewResults = false, currentUser, onDelete }) => {
  const st = STATUS_MAP[survey.status] || STATUS_MAP.taslak;
  const isExpired = survey.end_date && new Date(survey.end_date) < new Date();
  const borderColor = survey.status === 'aktif' ? 'border-l-emerald-500' : survey.status === 'taslak' ? 'border-l-slate-400' : survey.status === 'tamamlandi' ? 'border-l-blue-500' : 'border-l-amber-500';
  // Once Cevapla'ya yonlendirme onceligi:
  // - Anket aktif + suresi devam ediyor + henuz cevap verilmedi (veya allow_update) -> yanitla
  // - Cevap vermis vatandas/stk/muhtar yine yanitla'da kalir (orada "Zaten katildiniz" + public sonuc)
  // - Sadece view_results yetkili roller veya status=taslak/expired durumunda /sonuclar
  const canRespondNow = survey.status === 'aktif' && !isExpired && (!survey.has_responded || survey.allow_update);
  const goToResults = !canRespondNow && (canViewResults || survey.status === 'taslak' || isExpired);
  // Cevap vermis ama view_results yetkisi yok ise: yanitla sayfasinda (katildiniz ekrani) goster
  const showRespondedScreen = survey.has_responded && !canViewResults && !isExpired && survey.status === 'aktif';
  const linkTarget = (goToResults && !showRespondedScreen) ? `/panel/anketler/${survey.id}/sonuclar` : `/panel/anketler/${survey.id}/yanitle`;

  const isPrivileged = currentUser && ['admin', 'kent_konseyi_baskani', 'genel_sekreter'].includes(currentUser.role);
  const isCreator = currentUser && survey.created_by === currentUser.id;
  const createdAt = survey.created_at ? new Date(survey.created_at) : null;
  const withinWindow = createdAt && (Date.now() - createdAt.getTime()) <= 60 * 60 * 1000;
  const canDelete = isPrivileged || (isCreator && withinWindow);

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`"${survey.title}" anketini silmek istediginize emin misiniz? Bu islem geri alinamaz.`)) {
      onDelete(survey.id);
    }
  };

  return (
    <Link
      to={linkTarget}
      className="block group"
      data-testid={`survey-card-${survey.id}`}
    >
      <Card className={`border-l-4 ${borderColor} hover:shadow-md transition-all duration-200`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {showStatus && (
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${st.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    {st.label}
                  </span>
                )}
                {isExpired && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">Suresi Dolmus</span>}
                {survey.has_responded && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Katildiniz
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate text-base">{survey.title}</h3>
              {survey.description && (
                <p className="text-sm text-slate-500 mt-1.5 line-clamp-2">{survey.description}</p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 mt-1">
              {canDelete && (
                <button
                  onClick={handleDelete}
                  data-testid={`delete-survey-${survey.id}`}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  title={isPrivileged ? 'Anketi sil' : `Anketi sil (${Math.max(0, Math.ceil((60 * 60 * 1000 - (Date.now() - createdAt.getTime())) / 60000))} dk kaldi)`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4 pt-3 border-t text-xs text-slate-400">
            {survey.question_count !== undefined && (
              <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {survey.question_count} soru</span>
            )}
            {survey.response_count !== undefined && (
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {survey.response_count} katilim</span>
            )}
            {survey.start_date && (
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {survey.start_date}</span>
            )}
            {survey.end_date && (
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {survey.end_date}</span>
            )}
            {isCreator && !isPrivileged && withinWindow && (
              <span className="flex items-center gap-1 text-amber-500">
                <Trash2 className="w-3 h-3" /> Silme: {Math.max(0, Math.ceil((60 * 60 * 1000 - (Date.now() - createdAt.getTime())) / 60000))} dk
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default function SurveysPage() {
  const { hasPermission, user: currentUser } = useAuth();
  const navigate = useNavigate();
  usePageTitle('Anketler');
  const [surveys, setSurveys] = useState([]);
  const [myResponses, setMyResponses] = useState([]);
  const [allSurveys, setAllSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('aktif');

  const canCreate = hasPermission('surveys.create');
  const canViewResults = hasPermission('surveys.view_results');

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const status = tab === 'tumu' ? '' : tab === 'katildiklarim' ? '' : tab;
      const isMyResp = tab === 'katildiklarim';
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (isMyResp) params.set('my_responses', 'true');
      const res = await api.get(`/surveys?${params}`);
      if (isMyResp) {
        setMyResponses(res.data.surveys || []);
      } else {
        setSurveys(res.data.surveys || []);
        if (tab === 'tumu') setAllSurveys(res.data.surveys || []);
      }
    } catch {
      toast.error('Anketler yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchSurveys(); }, [fetchSurveys]);

  const handleDeleteSurvey = async (surveyId) => {
    try {
      await api.delete(`/surveys/${surveyId}`);
      toast.success('Anket basariyla silindi');
      fetchSurveys();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Anket silinemedi';
      toast.error(msg);
    }
  };

  // Load all surveys once for stats (all users)
  useEffect(() => {
    api.get('/surveys').then(res => {
      setAllSurveys(res.data.surveys || []);
    }).catch(() => {});
  }, []);

  const filtered = (tab === 'katildiklarim' ? myResponses : surveys).filter(s =>
    !search || s.title?.toLowerCase().includes(search.toLowerCase())
  );

  const totalCount = allSurveys.length;
  const activeCount = allSurveys.filter(s => s.status === 'aktif').length;
  const respondedCount = allSurveys.filter(s => s.has_responded).length;
  const totalResponses = allSurveys.reduce((sum, s) => sum + (s.response_count || 0), 0);

  const tabs = [
    { value: 'aktif', label: 'Aktif', show: true },
    { value: 'taslak', label: 'Taslak', show: canCreate },
    { value: 'katildiklarim', label: 'Katildiklarim', show: true },
    { value: 'tumu', label: 'Tumu', show: canCreate },
  ].filter(t => t.show);

  return (
    <div className="space-y-6" data-testid="surveys-page">
      {/* Page Header */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-violet-800 via-purple-900 to-violet-800 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Anketler</h1>
                <p className="text-sm text-white/50">Goruslerinizi paylasarak kararlara katkin</p>
              </div>
            </div>
            {canCreate && (
              <Button onClick={() => navigate('/panel/anketler/olustur')} className="bg-white/10 hover:bg-white/20 text-white border border-white/20" data-testid="create-survey-btn">
                <Plus className="w-4 h-4 mr-2" /> Yeni Anket
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-violet-50">
              <ListChecks className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalCount}</p>
              <p className="text-xs text-slate-500">Toplam Anket</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{activeCount}</p>
              <p className="text-xs text-slate-500">Aktif Anket</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-50">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{respondedCount}</p>
              <p className="text-xs text-slate-500">Katildiklarim</p>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-50">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalResponses}</p>
              <p className="text-xs text-slate-500">Toplam Katilim</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-1.5 flex-wrap" data-testid="survey-tabs">
          {tabs.map(t => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              data-testid={`tab-${t.value}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                tab === t.value
                  ? 'bg-violet-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Anket ara..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="survey-search" />
        </div>
      </div>

      {/* Survey List */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <ClipboardList className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-lg font-medium text-slate-900">Henuz anket bulunamadi</h3>
            <p className="text-sm text-slate-500 mt-1">Bu kategoride anket yok</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filtered.map(s => <SurveyCard key={s.id} survey={s} showStatus={tab === 'tumu' || tab === 'taslak'} canViewResults={canViewResults} currentUser={currentUser} onDelete={handleDeleteSurvey} />)}
        </div>
      )}
    </div>
  );
}
